import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Loading } from "components/common";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { readSomeLines } from "utils";
import {
  addMissingColumns,
  addFileName,
  addFileSamples,
  addColumnNames,
} from "state/reducers/communicationBatching";
import CheckmarkIcon from "assets/CheckmarkIcon";

const dedupJoin = (arrs) => {
  const output = [];
  arrs.forEach((arr) => {
    arr.forEach((val) => {
      if (!output.includes(val)) {
        output.push(val);
      }
    });
  });

  return output;
};

const csvStringToArray = (data) => {
  const re = /(,|\r?\n|\r|^)(?:"([^"]*(?:""[^"]*)*)"|([^,\r\n]*))/gi;
  const result = [[]];
  let matches;
  while ((matches = re.exec(data))) {
    if (matches[1].length && matches[1] !== ',') { result.push([]); }
    result[result.length - 1].push(
      matches[2] !== undefined ? matches[2].replace(/""/g, '"') : matches[3],
    );
  }

  return result[0];
};

const CSVUpload = ({ bypassed, setBypassed }) => {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const {
    emailTemplateVariables,
    textContentVariables,
    pushContentVariables,
    missingColumns,
    fileName,
  } = useSelector((state) => state.communicationBatching);

  const dedupedVariables = dedupJoin([
    emailTemplateVariables,
    textContentVariables,
    pushContentVariables,
  ]);

  const onDrop = useCallback(async (acceptedFiles) => {
    console.log("accepted file", acceptedFiles);
    if (!acceptedFiles.length) {
      return;
    }

    try {
      setLoading(true);
      let csvColumns = [];
      let sampleValues = [];
      // 12k records for sample...
      const csvLinesAsStrings = await readSomeLines(acceptedFiles[0], 12000); // header ["email,field1,field2,field3"]
      csvColumns = csvStringToArray(csvLinesAsStrings[0]);
      dispatch(addColumnNames(csvColumns));

      const linesAsValuesArr = csvLinesAsStrings.slice(1).reduce((acc, line) => {
        if (line && line.length > 1) {
          acc.push(csvStringToArray(line));
        }
        return acc;
      }, []);

      sampleValues = linesAsValuesArr.map((row) => {
        const colValObj = {};
        row.forEach((col, idx) => {
          colValObj[csvColumns[idx]] = col;
        });
        return colValObj;
      });

      if (csvColumns.length) {
        const newMissingColumns = ["email", ...dedupedVariables].filter(
          (templateVariable) => !csvColumns.includes(templateVariable),
        );
        if (newMissingColumns.length) {
          dispatch(addMissingColumns(newMissingColumns));
        }
        dispatch(addFileSamples(sampleValues));
      }

      dispatch(addFileName(acceptedFiles[0]));
      setSuccess(true);
      setError("");
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);
  const {
    getRootProps,
    getInputProps,
    isDragActive,
  } = useDropzone({ onDrop, accept: ".csv" });

  const removeFile = () => {
    dispatch(addFileName(null));
    setBypassed(null);
    setError("");
    setSuccess(false);
    dispatch(addMissingColumns([]));
  };
  return (
    <div>
      {missingColumns.length ? (
        <div>
          <div className="text-red-600">
            <p className="inline pr-1">
              {`The CSV does not include the column${
                missingColumns.length > 1 ? "s" : ""
              }`}
            </p>
            <p className="font-semibold inline">{missingColumns.join(", ")}</p>
          </div>
          <div className="flex items-center ml-auto">
            <input
              type="checkbox"
              className="align-middle mr-2"
              id="accounts"
              defaultChecked={bypassed}
              // checked={bypassed}
              onChange={() => {
                setBypassed(!bypassed);
              }}
            />
            <label htmlFor="accounts" className="pr-1">
              Bypass
            </label>
          </div>
        </div>
      ) : null}
      {success && !missingColumns.length ? (
        <div className="flex">
          <CheckmarkIcon className="pr-1" fill="#48bb78" />
          <p className="font-semibold text-green-500">
            The CSV file has all the required files
          </p>
        </div>
      ) : null}
      {loading ? <Loading /> : null}
      {fileName  ? (
        <div className="flex mt-4 items-center">
          <i className="fas fa-file-csv fa-lg pr-1" />
          <p>{fileName.name}</p>
          <i
            className="fas fa-times fa-lg pr-1 ml-2 hover:text-red-500"
            onClick={removeFile}
          />
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={classnames(
            "h-full mt-4 flex justify-center cursor-pointer rounded-lg",
            {
              "border-dashed border-gray-600 border": !isDragActive,
              "border-4 border-blue-500": isDragActive,
            },
          )}
        >
          <input {...getInputProps()} className="w-full h-full" />
          {isDragActive ? (
            <div className="flex items-center flex-col justify-center">
              <i className="fas fa-cloud-download-alt fa-5x pr-1 mb-2 text-gray-500" />
              <p>Drop here</p>
            </div>
          ) : (
            <div className="flex items-center flex-col justify-center">
              <i className="fas fa-cloud-download-alt fa-5x pr-1 mb-2 text-gray-500" />
              <p>Drag 'n' drop a CSV file here, or click to select files</p>
            </div>
          )}
        </div>
      )}
      <div className="">
        {!fileName ? (
          <div>
            <p className="inline">Please include the following columns: </p>
            <p className="inline italic font-semibold">
              {["email", ...dedupedVariables].join(", ")}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CSVUpload;
